import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  StickyNote, Plus, MessageCircle, Paperclip, Trash2, X, Download, Eye,
  Calendar, User, ChevronLeft, Loader2, Send, FileText, Pencil
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const WorkWall = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { postId } = useParams();
  
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showEditPostModal, setShowEditPostModal] = useState(false);
  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    color: 'yellow',
    visibility: [],
    documents: []
  });
  
  const [commentData, setCommentData] = useState({
    content: '',
    documents: []
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
    return { Authorization: `Bearer ${token}` };
  };

  const colors = [
    { id: 'yellow', label: 'Sarı', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-900' },
    { id: 'blue', label: 'Mavi', bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-900' },
    { id: 'green', label: 'Yeşil', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-900' },
    { id: 'pink', label: 'Pembe', bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-900' },
    { id: 'orange', label: 'Turuncu', bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-900' },
    { id: 'purple', label: 'Mor', bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-900' },
  ];

  const getColorClasses = (colorId) => {
    return colors.find(c => c.id === colorId) || colors[0];
  };

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/wall/posts`, { headers: getAuthHeaders() });
      setPosts(response.data || []);
      
      // Mark all posts as seen when page loads
      try {
        await axios.put(`${API}/api/wall/mark-all-seen`, {}, { headers: getAuthHeaders() });
      } catch (err) {
        console.error('Postlar görüldü işaretlenemedi:', err);
      }
    } catch (error) {
      console.error('Posts yüklenemedi:', error);
      toast.error('Paylaşımlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch single post with comments
  const fetchPostDetail = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/wall/posts/${id}`, { headers: getAuthHeaders() });
      setSelectedPost(response.data);
      
      // Mark post and comments as seen
      await axios.put(`${API}/api/wall/posts/${id}/mark-seen`, {}, { headers: getAuthHeaders() });
    } catch (error) {
      console.error('Post detayı yüklenemedi:', error);
      toast.error('Detay yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch users for visibility
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/wall/users`, { headers: getAuthHeaders() });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    if (postId) {
      fetchPostDetail(postId);
    } else {
      fetchPosts();
    }
  }, [postId, fetchPosts, fetchPostDetail, fetchUsers]);

  // Handle file upload
  const handleFileUpload = async (e, isComment = false) => {
    const files = Array.from(e.target.files || []);
    const newDocs = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newDocs.push({
        name: file.name,
        type: file.type,
        data: base64
      });
    }
    
    if (isComment) {
      setCommentData(prev => ({ ...prev, documents: [...prev.documents, ...newDocs] }));
    } else {
      setFormData(prev => ({ ...prev, documents: [...prev.documents, ...newDocs] }));
    }
  };

  // Create post
  const handleCreatePost = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Konu ve açıklama zorunludur');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/wall/posts`, formData, { headers: getAuthHeaders() });
      toast.success('Paylaşım oluşturuldu');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', color: 'yellow', visibility: [], documents: [] });
      
      if (postId) {
        fetchPostDetail(postId);
      } else {
        fetchPosts();
      }
    } catch (error) {
      toast.error('Paylaşım oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  // Add comment
  const handleAddComment = async () => {
    if (!commentData.content.trim()) {
      toast.error('Yorum yazın');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/wall/posts/${selectedPost.id}/comments`, commentData, { headers: getAuthHeaders() });
      toast.success('Yorum eklendi');
      setShowCommentModal(false);
      setCommentData({ content: '', documents: [] });
      fetchPostDetail(selectedPost.id);
    } catch (error) {
      toast.error('Yorum eklenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete post
  const handleDeletePost = async (id) => {
    if (!window.confirm('Bu paylaşımı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/api/wall/posts/${id}`, { headers: getAuthHeaders() });
      toast.success('Paylaşım silindi');
      if (postId) {
        navigate('/work/wall');
      } else {
        fetchPosts();
      }
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/api/wall/comments/${commentId}`, { headers: getAuthHeaders() });
      toast.success('Yorum silindi');
      fetchPostDetail(selectedPost.id);
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  // Open edit post modal
  const openEditPostModal = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      description: post.description || '',
      color: post.color || 'yellow',
      visibility: post.visibility || [],
      documents: post.documents || []
    });
    setShowEditPostModal(true);
  };

  // Handle edit post submit
  const handleEditPost = async () => {
    if (!formData.title.trim()) {
      toast.error('Başlık gerekli');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/api/wall/posts/${editingPost.id}`, formData, { headers: getAuthHeaders() });
      toast.success('Paylaşım güncellendi');
      setShowEditPostModal(false);
      setEditingPost(null);
      if (postId) {
        fetchPostDetail(postId);
      } else {
        fetchPosts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncellenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit comment modal
  const openEditCommentModal = (comment) => {
    setEditingComment(comment);
    setCommentData({
      content: comment.content || '',
      documents: comment.documents || []
    });
    setShowEditCommentModal(true);
  };

  // Handle edit comment submit
  const handleEditComment = async () => {
    if (!commentData.content.trim()) {
      toast.error('Yorum içeriği gerekli');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/api/wall/comments/${editingComment.id}`, commentData, { headers: getAuthHeaders() });
      toast.success('Yorum güncellendi');
      setShowEditCommentModal(false);
      setEditingComment(null);
      fetchPostDetail(selectedPost.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncellenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // Group posts by date
  const groupPostsByDate = (posts) => {
    const groups = {};
    posts.forEach(post => {
      const date = new Date(post.created_at).toLocaleDateString('tr-TR');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(post);
    });
    return groups;
  };

  // Render post-it note
  const renderPostIt = (post, isDetail = false) => {
    const colorClass = getColorClasses(post.color);
    const hasNewContent = post.is_new || (post.new_comment_count > 0);
    
    return (
      <div 
        key={post.id}
        className={`relative ${colorClass.bg} ${colorClass.border} border-2 rounded-lg p-6 shadow-lg transform hover:scale-105 transition-all cursor-pointer ${hasNewContent ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}
        style={{ 
          fontFamily: "'Caveat', 'Patrick Hand', cursive",
          minHeight: isDetail ? 'auto' : '270px',
          maxWidth: isDetail ? '100%' : '420px'
        }}
        onClick={() => !isDetail && navigate(`/work/wall/${post.id}`)}
      >
        {/* Pin effect */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-red-500 rounded-full shadow-md border-2 border-red-700" />
        
        {/* YENİ badge */}
        {hasNewContent && !isDetail && (
          <div className="absolute top-2 right-2 z-20">
            <span className="bg-red-600 text-white text-sm font-bold px-3 py-1.5 rounded-full animate-pulse shadow-lg border-2 border-white">
              {post.is_new ? 'YENİ' : `${post.new_comment_count} yeni yorum`}
            </span>
          </div>
        )}
        
        {/* Delete button for owner */}
        {post.is_owner && (
          <div className="absolute top-3 right-3 flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); openEditPostModal(post); }}
              className="text-gray-500 hover:text-blue-600 transition-colors"
              title="Düzenle"
            >
              <Pencil className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDeletePost(post.id); }}
              className="text-gray-500 hover:text-red-600 transition-colors"
              title="Sil"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {/* Content */}
        <div className="pt-3">
          <h3 className={`text-2xl font-bold ${colorClass.text} mb-3`} style={{ fontFamily: "'Caveat', cursive" }}>
            {post.title || ''}
          </h3>
          <p className={`text-lg ${colorClass.text} mb-4 whitespace-pre-wrap`} style={{ fontFamily: "'Caveat', cursive" }}>
            {isDetail ? (post.description || '') : ((post.description || '').length > 100 ? (post.description || '').slice(0, 100) + '...' : (post.description || ''))}
          </p>
          
          {/* Footer */}
          <div className="border-t border-current/20 pt-3 mt-3">
            <div className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2 opacity-70">
                <Calendar className="h-4 w-4" />
                <span style={{ fontFamily: "'Caveat', cursive" }}>
                  {new Date(post.created_at).toLocaleDateString('tr-TR')}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {post.documents?.length > 0 && (
                  <div className="flex items-center gap-1 opacity-70">
                    <Paperclip className="h-4 w-4" />
                    <span>{post.total_documents || post.documents.length}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 opacity-70">
                  <MessageCircle className="h-4 w-4" />
                  <span>{post.comment_count || 0}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 opacity-60 text-sm">
              <User className="h-4 w-4" />
              <span style={{ fontFamily: "'Caveat', cursive" }}>{post.created_by_name}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Detail view (cork board)
  if (postId && selectedPost) {
    const colorClass = getColorClasses(selectedPost.color);
    const groupedComments = groupPostsByDate(selectedPost.comments || []);
    
    return (
      <div 
        className="min-h-screen p-6"
        style={{
          background: `
            linear-gradient(90deg, rgba(139,90,43,0.1) 1px, transparent 1px),
            linear-gradient(rgba(139,90,43,0.1) 1px, transparent 1px),
            #d4a574
          `,
          backgroundSize: '20px 20px, 20px 20px, 100% 100%'
        }}
      >
        {/* Back button */}
        <Button 
          variant="outline" 
          onClick={() => navigate('/work/wall')}
          className="mb-4 bg-white/80"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Geri Dön
        </Button>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <>
            {/* Main post */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className={`${colorClass.bg} ${colorClass.border} border-2 rounded-lg p-6 shadow-xl relative`}>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-red-500 rounded-full shadow-md border-2 border-red-700" />
                
                {selectedPost.is_owner && (
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button
                      onClick={() => openEditPostModal(selectedPost)}
                      className="text-gray-500 hover:text-blue-600"
                      title="Düzenle"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeletePost(selectedPost.id)}
                      className="text-gray-500 hover:text-red-600"
                      title="Sil"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                )}
                
                <h1 className={`text-3xl font-bold ${colorClass.text} mb-4 pt-2`} style={{ fontFamily: "'Caveat', cursive" }}>
                  {selectedPost.title}
                </h1>
                <p className={`text-xl ${colorClass.text} whitespace-pre-wrap mb-4`} style={{ fontFamily: "'Caveat', cursive" }}>
                  {selectedPost.description}
                </p>
                
                {/* Documents */}
                {selectedPost.documents?.length > 0 && (
                  <div className="mb-4">
                    <Label className="text-sm opacity-70">Belgeler:</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedPost.documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc.data || doc.url}
                          download={doc.name}
                          className="flex items-center gap-1 px-2 py-1 bg-white/50 rounded text-sm hover:bg-white/80"
                        >
                          <FileText className="h-3 w-3" />
                          {doc.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="border-t border-current/20 pt-3 flex items-center justify-between text-sm opacity-70">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span style={{ fontFamily: "'Caveat', cursive" }}>{selectedPost.created_by_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(selectedPost.created_at).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="max-w-2xl mx-auto mb-6 flex gap-2 justify-center">
              <Button onClick={() => setShowCommentModal(true)} className="bg-amber-600 hover:bg-amber-700">
                <MessageCircle className="h-4 w-4 mr-2" />
                Yorum Ekle
              </Button>
            </div>

            {/* Comments as post-its */}
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Yorumlar ({selectedPost.comments?.length || 0})
              </h2>
              
              {Object.entries(groupedComments).map(([date, comments]) => (
                <div key={date} className="mb-6">
                  <div className="text-sm text-amber-800 mb-2 font-semibold">{date}</div>
                  <div className="flex flex-wrap gap-6">
                    {comments.map(comment => (
                      <div 
                        key={comment.id}
                        className="bg-yellow-100 border-2 border-yellow-300 rounded-lg p-6 shadow-lg relative"
                        style={{ fontFamily: "'Caveat', cursive", minWidth: '320px', maxWidth: '480px' }}
                      >
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-red-400 rounded-full shadow border border-red-500" />
                        
                        {comment.created_by === user?.id && (
                          <div className="absolute top-3 right-3 flex gap-2">
                            <button
                              onClick={() => openEditCommentModal(comment)}
                              className="text-gray-400 hover:text-blue-600"
                              title="Düzenle"
                            >
                              <Pencil className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-gray-400 hover:text-red-600"
                              title="Sil"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                        
                        <p className="text-2xl text-yellow-900 whitespace-pre-wrap pt-2">{comment.content}</p>
                        
                        {comment.documents?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {comment.documents.map((doc, idx) => (
                              <a
                                key={idx}
                                href={doc.data || doc.url}
                                download={doc.name}
                                className="flex items-center gap-2 px-3 py-1.5 bg-white/50 rounded text-base hover:bg-white/80"
                              >
                                <Paperclip className="h-4 w-4" />
                                {doc.name}
                              </a>
                            ))}
                          </div>
                        )}
                        
                        <div className="mt-4 pt-3 border-t border-yellow-400/50 text-base text-yellow-800 flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {comment.created_by_name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {(!selectedPost.comments || selectedPost.comments.length === 0) && (
                <div className="text-center py-8 text-amber-700 opacity-70">
                  Henüz yorum yok. İlk yorumu siz ekleyin!
                </div>
              )}
            </div>
          </>
        )}

        {/* Comment Modal */}
        <Dialog open={showCommentModal} onOpenChange={setShowCommentModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <MessageCircle className="h-5 w-5" />
                Yorum Ekle
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Yorumunuz *</Label>
                <Textarea
                  value={commentData.content}
                  onChange={(e) => setCommentData({ ...commentData, content: e.target.value })}
                  placeholder="Yorumunuzu yazın..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Belge Ekle</Label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e, true)}
                  className="mt-1 w-full text-sm"
                />
                {commentData.documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {commentData.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                        <span className="truncate">{doc.name}</span>
                        <button onClick={() => setCommentData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }))}>
                          <X className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCommentModal(false)}>İptal</Button>
              <Button onClick={handleAddComment} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Gönder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Post Modal (also used in detail view) */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <StickyNote className="h-5 w-5" />
                Yeni Paylaşım Oluştur
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Konu *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Paylaşım konusu"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Açıklama *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Açıklama yazın..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Renk Seçin</Label>
                <div className="flex gap-2 mt-2">
                  {colors.map(color => (
                    <button
                      key={color.id}
                      onClick={() => setFormData({ ...formData, color: color.id })}
                      className={`w-8 h-8 rounded-full ${color.bg} border-2 ${formData.color === color.id ? 'ring-2 ring-offset-2 ring-amber-500' : ''} ${color.border}`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label>Kimler Görebilsin?</Label>
                <div className="mt-2 max-h-32 overflow-y-auto border rounded p-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Checkbox 
                      checked={formData.visibility.length === 0}
                      onCheckedChange={() => setFormData({ ...formData, visibility: [] })}
                    />
                    <span>Herkes görebilsin</span>
                  </div>
                  {users.filter(u => u.id !== user?.id).map(u => (
                    <div key={u.id} className="flex items-center gap-2 text-sm">
                      <Checkbox 
                        checked={formData.visibility.includes(u.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ ...formData, visibility: [...formData.visibility, u.id] });
                          } else {
                            setFormData({ ...formData, visibility: formData.visibility.filter(id => id !== u.id) });
                          }
                        }}
                      />
                      <span>{u.name} {u.position && `(${u.position})`}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label>Belgeler</Label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e, false)}
                  className="mt-1 w-full text-sm"
                />
                {formData.documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {formData.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                        <span className="truncate">{doc.name}</span>
                        <button onClick={() => setFormData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }))}>
                          <X className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>İptal</Button>
              <Button onClick={handleCreatePost} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Oluştur
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main wall view (cork board with all posts)
  const groupedPosts = groupPostsByDate(posts);

  return (
    <div 
      className="min-h-screen p-6"
      style={{
        background: `
          linear-gradient(90deg, rgba(139,90,43,0.1) 1px, transparent 1px),
          linear-gradient(rgba(139,90,43,0.1) 1px, transparent 1px),
          #d4a574
        `,
        backgroundSize: '20px 20px, 20px 20px, 100% 100%'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-100 rounded-lg">
            <StickyNote className="h-8 w-8 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-amber-900">Şube Duvarı</h1>
            <p className="text-amber-700">Paylaşımlar ve duyurular</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
          <Plus className="h-4 w-4" />
          Paylaşım Ekle
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <StickyNote className="h-16 w-16 mx-auto text-amber-400 mb-4" />
          <h3 className="text-xl font-semibold text-amber-800">Henüz paylaşım yok</h3>
          <p className="text-amber-600 mb-4">İlk paylaşımı siz oluşturun!</p>
          <Button onClick={() => setShowCreateModal(true)} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-2" />
            Paylaşım Oluştur
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedPosts).map(([date, datePosts]) => (
            <div key={date}>
              <div className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {date}
              </div>
              <div className="flex flex-wrap gap-6">
                {datePosts.map(post => renderPostIt(post))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <StickyNote className="h-5 w-5" />
              Yeni Paylaşım Oluştur
            </DialogTitle>
            <DialogDescription>
              Post-it tarzında bir paylaşım oluşturun
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Konu *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Paylaşım konusu"
                className="mt-1"
                data-testid="wall-title-input"
              />
            </div>
            <div>
              <Label>Açıklama *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Açıklama yazın..."
                rows={4}
                className="mt-1"
                data-testid="wall-description-input"
              />
            </div>
            <div>
              <Label>Renk Seçin</Label>
              <div className="flex gap-2 mt-2">
                {colors.map(color => (
                  <button
                    key={color.id}
                    onClick={() => setFormData({ ...formData, color: color.id })}
                    className={`w-8 h-8 rounded-full ${color.bg} border-2 ${formData.color === color.id ? 'ring-2 ring-offset-2 ring-amber-500' : ''} ${color.border}`}
                    title={color.label}
                    data-testid={`color-${color.id}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Kimler Görebilsin?</Label>
              <div className="mt-2 max-h-32 overflow-y-auto border rounded p-2 space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <Checkbox 
                    checked={formData.visibility.length === 0}
                    onCheckedChange={() => setFormData({ ...formData, visibility: [] })}
                  />
                  <span>Herkes görebilsin</span>
                </div>
                {users.filter(u => u.id !== user?.id).map(u => (
                  <div key={u.id} className="flex items-center gap-2 text-sm">
                    <Checkbox 
                      checked={formData.visibility.includes(u.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, visibility: [...formData.visibility, u.id] });
                        } else {
                          setFormData({ ...formData, visibility: formData.visibility.filter(id => id !== u.id) });
                        }
                      }}
                    />
                    <span>{u.name} {u.position && `(${u.position})`}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Belgeler</Label>
              <input
                type="file"
                multiple
                onChange={(e) => handleFileUpload(e, false)}
                className="mt-1 w-full text-sm"
              />
              {formData.documents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="truncate">{doc.name}</span>
                      <button onClick={() => setFormData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }))}>
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>İptal</Button>
            <Button onClick={handleCreatePost} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="create-wall-post-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Post Modal */}
      <Dialog open={showEditPostModal} onOpenChange={setShowEditPostModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Paylaşımı Düzenle
            </DialogTitle>
            <DialogDescription>Paylaşım bilgilerini güncelleyin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Başlık *</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Paylaşım başlığı"
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Paylaşım açıklaması"
                rows={4}
              />
            </div>
            <div>
              <Label>Renk</Label>
              <div className="flex gap-2 mt-1">
                {colors.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setFormData({ ...formData, color: c.id })}
                    className={`w-8 h-8 rounded-full ${c.bg} ${c.border} border-2 ${formData.color === c.id ? 'ring-2 ring-offset-2 ring-amber-500' : ''}`}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Belgeler</Label>
              <input
                type="file"
                multiple
                onChange={(e) => handleFileUpload(e, false)}
                className="mt-1 w-full text-sm"
              />
              {formData.documents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="truncate">{doc.name}</span>
                      <button onClick={() => setFormData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }))}>
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Görecek Kişiler</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                <div className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded">
                  <Checkbox 
                    id="edit-all-users"
                    checked={formData.visibility.length === 0}
                    onCheckedChange={() => setFormData({ ...formData, visibility: [] })}
                  />
                  <label htmlFor="edit-all-users" className="text-sm font-medium cursor-pointer">Herkes</label>
                </div>
                {users.map(u => (
                  <div key={u.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded">
                    <Checkbox 
                      id={`edit-user-${u.id}`}
                      checked={formData.visibility.includes(u.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, visibility: [...formData.visibility, u.id] });
                        } else {
                          setFormData({ ...formData, visibility: formData.visibility.filter(id => id !== u.id) });
                        }
                      }}
                    />
                    <label htmlFor={`edit-user-${u.id}`} className="text-sm cursor-pointer">{u.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditPostModal(false); setEditingPost(null); }}>İptal</Button>
            <Button onClick={handleEditPost} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Comment Modal */}
      <Dialog open={showEditCommentModal} onOpenChange={setShowEditCommentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Yorumu Düzenle
            </DialogTitle>
            <DialogDescription>Yorum içeriğini güncelleyin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Yorum *</Label>
              <Textarea 
                value={commentData.content}
                onChange={(e) => setCommentData({ ...commentData, content: e.target.value })}
                placeholder="Yorumunuz"
                rows={4}
              />
            </div>
            <div>
              <Label>Belgeler</Label>
              <input
                type="file"
                multiple
                onChange={(e) => handleFileUpload(e, true)}
                className="mt-1 w-full text-sm"
              />
              {commentData.documents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {commentData.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="truncate">{doc.name}</span>
                      <button onClick={() => setCommentData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }))}>
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditCommentModal(false); setEditingComment(null); }}>İptal</Button>
            <Button onClick={handleEditComment} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkWall;
